import { z } from 'zod';
// Enums
export var AttendanceStatus;
(function (AttendanceStatus) {
    AttendanceStatus["PRESENT"] = "PRESENT";
    AttendanceStatus["ABSENT"] = "ABSENT";
    AttendanceStatus["LATE"] = "LATE";
    AttendanceStatus["HALF_DAY"] = "HALF_DAY";
    AttendanceStatus["SICK_LEAVE"] = "SICK_LEAVE";
    AttendanceStatus["CASUAL_LEAVE"] = "CASUAL_LEAVE";
    AttendanceStatus["EXCUSED"] = "EXCUSED";
})(AttendanceStatus || (AttendanceStatus = {}));
export var AttendanceType;
(function (AttendanceType) {
    AttendanceType["REGULAR"] = "REGULAR";
    AttendanceType["OVERTIME"] = "OVERTIME";
    AttendanceType["HOLIDAY"] = "HOLIDAY";
    AttendanceType["MAKEUP"] = "MAKEUP";
})(AttendanceType || (AttendanceType = {}));
// Zod Schemas
export const LocationSchema = z.object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    accuracy: z.number().optional(),
    address: z.string().optional(),
});
export const AttendanceRecordSchema = z.object({
    id: z.string().uuid(),
    date: z.date(),
    status: z.nativeEnum(AttendanceStatus),
    type: z.nativeEnum(AttendanceType).default(AttendanceType.REGULAR),
    checkInTime: z.date().optional(),
    checkOutTime: z.date().optional(),
    location: LocationSchema.optional(),
    photoUrl: z.string().url().optional(),
    videoUrl: z.string().url().optional(),
    remarks: z.string().optional(),
    isManualEntry: z.boolean().default(false),
    manualEntryReason: z.string().optional(),
    approvedBy: z.string().uuid().optional(),
    approvedAt: z.date().optional(),
    // Relationships
    userId: z.string().uuid(),
    tenantId: z.string().uuid(),
    studentId: z.string().uuid().optional(),
    classId: z.string().uuid().optional(),
    subjectId: z.string().uuid().optional(),
    // Metadata
    deviceInfo: z.record(z.any()).optional(),
    ipAddress: z.string().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateAttendanceSchema = AttendanceRecordSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    approvedBy: true,
    approvedAt: true,
});
export const UpdateAttendanceSchema = CreateAttendanceSchema.partial();
export const BulkAttendanceSchema = z.object({
    date: z.date(),
    classId: z.string().uuid().optional(),
    subjectId: z.string().uuid().optional(),
    records: z.array(z.object({
        userId: z.string().uuid(),
        studentId: z.string().uuid().optional(),
        status: z.nativeEnum(AttendanceStatus),
        remarks: z.string().optional(),
    })),
});
// Attendance Calculator
export class AttendanceCalculator {
    static calculateWorkingHours(checkIn, checkOut) {
        if (!checkOut)
            return 0;
        const diffMs = checkOut.getTime() - checkIn.getTime();
        return Math.max(0, diffMs / (1000 * 60 * 60)); // Convert to hours
    }
    static isLate(checkIn, workingHours, lateThreshold) {
        const [startHour, startMinute] = workingHours.start.split(':').map(Number);
        const workStart = new Date(checkIn);
        workStart.setHours(startHour || 0, startMinute || 0, 0, 0);
        const diffMs = checkIn.getTime() - workStart.getTime();
        const diffMinutes = diffMs / (1000 * 60);
        return diffMinutes > lateThreshold;
    }
    static calculateAttendancePercentage(presentDays, totalDays) {
        if (totalDays === 0)
            return 0;
        return Math.round((presentDays / totalDays) * 100 * 100) / 100; // Round to 2 decimal places
    }
    static isWithinGeoFence(userLocation, allowedLocations, radius) {
        return allowedLocations.some(location => {
            const distance = this.calculateDistance(userLocation, location);
            return distance <= radius;
        });
    }
    static calculateDistance(point1, point2) {
        const R = 6371e3; // Earth's radius in meters
        const φ1 = point1.latitude * Math.PI / 180;
        const φ2 = point2.latitude * Math.PI / 180;
        const Δφ = (point2.latitude - point1.latitude) * Math.PI / 180;
        const Δλ = (point2.longitude - point1.longitude) * Math.PI / 180;
        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
                Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Distance in meters
    }
    static generateStatistics(records) {
        const totalDays = records.length;
        const presentDays = records.filter(r => r.status === AttendanceStatus.PRESENT).length;
        const absentDays = records.filter(r => r.status === AttendanceStatus.ABSENT).length;
        const lateDays = records.filter(r => r.status === AttendanceStatus.LATE).length;
        const halfDays = records.filter(r => r.status === AttendanceStatus.HALF_DAY).length;
        const leaveDays = records.filter(r => [AttendanceStatus.SICK_LEAVE, AttendanceStatus.CASUAL_LEAVE].includes(r.status)).length;
        const workingRecords = records.filter(r => r.checkInTime && r.checkOutTime);
        const totalWorkingHours = workingRecords.reduce((total, record) => {
            return total + this.calculateWorkingHours(record.checkInTime, record.checkOutTime);
        }, 0);
        const checkInTimes = records
            .filter(r => r.checkInTime)
            .map(r => r.checkInTime.getHours() * 60 + r.checkInTime.getMinutes());
        const checkOutTimes = records
            .filter(r => r.checkOutTime)
            .map(r => r.checkOutTime.getHours() * 60 + r.checkOutTime.getMinutes());
        const averageCheckInMinutes = checkInTimes.length > 0
            ? checkInTimes.reduce((a, b) => a + b, 0) / checkInTimes.length
            : 0;
        const averageCheckOutMinutes = checkOutTimes.length > 0
            ? checkOutTimes.reduce((a, b) => a + b, 0) / checkOutTimes.length
            : 0;
        return {
            totalDays,
            presentDays,
            absentDays,
            lateDays,
            halfDays,
            leaveDays,
            attendancePercentage: this.calculateAttendancePercentage(presentDays, totalDays),
            averageCheckInTime: this.minutesToTimeString(averageCheckInMinutes),
            averageCheckOutTime: this.minutesToTimeString(averageCheckOutMinutes),
            totalWorkingHours: Math.round(totalWorkingHours * 100) / 100,
            overtimeHours: 0, // Calculate based on rules
        };
    }
    static minutesToTimeString(minutes) {
        const hours = Math.floor(minutes / 60);
        const mins = Math.floor(minutes % 60);
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    }
}
// Attendance Validator
export class AttendanceValidator {
    static validateCheckIn(location, rules) {
        const errors = [];
        // Validate geo-fencing
        if (rules.geoFencing.enabled && location) {
            const allowedLocations = rules.geoFencing.locations.map(l => l.coordinates);
            const radius = rules.geoFencing.locations[0]?.radius || 100;
            if (!AttendanceCalculator.isWithinGeoFence(location, allowedLocations, radius)) {
                errors.push('Location is outside allowed area');
            }
        }
        return {
            valid: errors.length === 0,
            errors,
        };
    }
    static validateAttendanceRecord(record) {
        const errors = [];
        // Validate time consistency
        if (record.checkInTime && record.checkOutTime) {
            if (record.checkOutTime <= record.checkInTime) {
                errors.push('Check-out time must be after check-in time');
            }
        }
        // Validate status consistency
        if (record.status === AttendanceStatus.PRESENT && !record.checkInTime) {
            errors.push('Present status requires check-in time');
        }
        return {
            valid: errors.length === 0,
            errors,
        };
    }
}
//# sourceMappingURL=Attendance.js.map